import { useState } from 'react';
import { Users, Plus, Gift, Share2, Trophy } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { LeaderboardCard } from '@/components/ui/LeaderboardCard';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useReferrals } from '@/hooks/useReferrals';
import { useProfile } from '@/hooks/useProfile';
import { VEHICLE_CATEGORY_LABELS } from '@/types';
import type { VehicleCategory } from '@/types';
import toast from 'react-hot-toast';

export function ReferralsPage() {
  const { referrals, tiers, leaderboard, loading, create } = useReferrals();
  const { profile } = useProfile();
  const [showAdd, setShowAdd] = useState(false);
  const [showTiers, setShowTiers] = useState(false);
  const [form, setForm] = useState({ referred_name: '', referred_phone: '', referred_vehicle_category: '', notes: '' });
  const [saving, setSaving] = useState(false);

  if (loading) return <LoadingSpinner />;

  const handleCreate = async () => {
    if (!form.referred_name || !form.referred_phone) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }
    try {
      setSaving(true);
      const ref = await create({
        referred_name: form.referred_name,
        referred_phone: form.referred_phone,
        referred_vehicle_category: form.referred_vehicle_category || undefined,
        notes: form.notes || undefined,
      });
      setShowAdd(false);
      setForm({ referred_name: '', referred_phone: '', referred_vehicle_category: '', notes: '' });
      toast.success(`¡Referido enviado! Código: ${ref.referral_code}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl tracking-wider">REFERIDOS</h1>
        <Button size="sm" onClick={() => setShowAdd(true)}>
          <Plus size={14} className="mr-1" /> Referir
        </Button>
      </div>

      {/* Summary card */}
      <Card className="bg-brand-black text-brand-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-brand-yellow font-heading text-lg tracking-wider">PROGRAMA DE REFERIDOS</p>
            <p className="text-sm text-gray-400">Gana dinero por cada conductor que refieras</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-brand-yellow">{referrals.length}</p>
            <p className="text-xs text-gray-400">referidos</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="mt-3 border-brand-yellow text-brand-yellow" onClick={() => setShowTiers(true)}>
          <Gift size={14} className="mr-1" /> Ver recompensas
        </Button>
      </Card>

      {/* Referrals list */}
      {referrals.length === 0 ? (
        <EmptyState
          title="Sin referidos"
          description="Refiere a un conductor y gana recompensas cuando complete sus primeras rutas."
          icon={<Users size={28} className="text-gray-400" />}
          action={<Button onClick={() => setShowAdd(true)}>Referir conductor</Button>}
        />
      ) : (
        <div className="space-y-2">
          {referrals.map(ref => (
            <Card key={ref.id} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-yellow rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-brand-black">{ref.referred_name.charAt(0).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{ref.referred_name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{ref.referral_code}</span>
                  {ref.referred_vehicle_category && (
                    <span>• {VEHICLE_CATEGORY_LABELS[ref.referred_vehicle_category as VehicleCategory]}</span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <StatusBadge status={ref.status} />
                {ref.reward_amount && (
                  <p className="text-xs font-semibold text-brand-black mt-1">${ref.reward_amount.toLocaleString()}</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div>
          <h2 className="font-heading text-xl tracking-wider mb-3 flex items-center gap-2">
            <Trophy size={18} className="text-brand-yellow" /> TOP REFERENTES
          </h2>
          <div className="space-y-2">
            {leaderboard.map(entry => (
              <LeaderboardCard
                key={entry.driver_profile_id}
                rank={entry.rank}
                name={entry.display_name || 'Conductor'}
                photoUrl={entry.profile_photo_url}
                points={entry.total_referrals}
                isCurrentUser={entry.driver_profile_id === profile?.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* New referral modal */}
      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Referir conductor">
        <div className="space-y-4">
          <Input
            label="Nombre del conductor"
            placeholder="Juan Pérez"
            value={form.referred_name}
            onChange={(e) => setForm(p => ({ ...p, referred_name: e.target.value }))}
          />
          <Input
            label="Teléfono"
            placeholder="55 1234 5678"
            value={form.referred_phone}
            onChange={(e) => setForm(p => ({ ...p, referred_phone: e.target.value }))}
          />
          <Select
            label="Tipo de vehículo"
            value={form.referred_vehicle_category}
            onChange={(e) => setForm(p => ({ ...p, referred_vehicle_category: e.target.value }))}
            options={Object.entries(VEHICLE_CATEGORY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          />
          <Button fullWidth onClick={handleCreate} loading={saving}>
            <Share2 size={16} className="mr-1" /> Enviar referido
          </Button>
        </div>
      </Modal>

      {/* Reward tiers modal */}
      <Modal isOpen={showTiers} onClose={() => setShowTiers(false)} title="Recompensas por referido">
        <div className="space-y-2">
          {tiers.map(tier => (
            <div key={tier.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-xl">
              <div>
                <p className="text-sm font-semibold">{VEHICLE_CATEGORY_LABELS[tier.vehicle_category as VehicleCategory]}</p>
                <p className="text-xs text-gray-500">Mín. {tier.min_completed_routes} rutas</p>
              </div>
              <p className="text-lg font-bold text-brand-black">${tier.reward_amount.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
